import React, { createContext, useContext, useState, useCallback } from 'react';
import axios from 'axios';
import { useAuthRefresh } from '../lib/useAuthRefresh';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const RecruitmentContext = createContext(null);

// Query string for a report. `period` is { start, end, basis }: ISO instants
// bounding the period (end exclusive) and, where the report has one, what the
// dates select. Empty values are left out.
const reportQuery = (period = {}, extra = {}) => {
  const params = new URLSearchParams();
  Object.entries({ ...extra, ...period }).forEach(([key, value]) => {
    if (value) params.append(key, value);
  });
  return params.toString();
};

export const useRecruitment = () => {
  const context = useContext(RecruitmentContext);
  if (!context) {
    throw new Error('useRecruitment must be used within a RecruitmentProvider');
  }
  return context;
};

export const RecruitmentProvider = ({ children, token }) => {
  // Silent access-token refresh so this sub-app tab survives the 15-min
  // access-token TTL. Installs axios interceptors that override
  // Authorization for /api/recruitment/* with a live token, and hits
  // /api/auth/refresh every 12 min via the HttpOnly refresh cookie.
  useAuthRefresh(token, { backendUrl: BACKEND_URL, apiPrefix: '/api/recruitment' });

  const [loading, setLoading] = useState(false);
  const [jobs, setJobs] = useState([]);
  const [candidates, setCandidates] = useState([]);
  const [pipelineTemplates, setPipelineTemplates] = useState([]);
  const [interviews, setInterviews] = useState([]);
  const [employees, setEmployees] = useState([]);

  const isReady = Boolean(token);

  const headers = {
    'Content-Type': 'application/json',
    'Authorization': token ? `Bearer ${token}` : ''
  };

  // ============ DASHBOARD ============
  const getDashboardStats = useCallback(async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/recruitment/dashboard/stats`, { headers });
      return response.data;
    } catch (err) {
      console.error('Failed to fetch dashboard stats:', err);
      return null;
    }
  }, [token]);

  const getEnhancedDashboardStats = useCallback(async (fromDate, toDate) => {
    try {
      const params = new URLSearchParams();
      if (fromDate) params.append('from_date', fromDate);
      if (toDate) params.append('to_date', toDate);
      const response = await axios.get(`${BACKEND_URL}/api/recruitment/dashboard/enhanced-stats?${params.toString()}`, { headers });
      return response.data;
    } catch (err) {
      console.error('Failed to fetch enhanced dashboard stats:', err);
      return null;
    }
  }, [token]);

  // ============ REPORTS ============
  // All return { success, data } / { success: false, error } so a report can
  // tell "failed" apart from "no rows" and offer a retry. None touches shared
  // state (`loading`, `jobs`, `candidates`): a report is read-only and must
  // not swap out lists other tabs are showing. `period`: see reportQuery.
  const getStageSummaryReport = useCallback(async (period) => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/recruitment/reports/stage-summary?${reportQuery(period)}`, { headers });
      return { success: true, data: response.data };
    } catch (err) {
      return { success: false, error: err.response?.data?.detail || 'Failed to load the report' };
    }
  }, [token]);

  // How many candidates have passed through each stage (stage history).
  const getStageFunnelReport = useCallback(async (period) => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/recruitment/reports/stage-funnel?${reportQuery(period)}`, { headers });
      return { success: true, data: response.data };
    } catch (err) {
      return { success: false, error: err.response?.data?.detail || 'Failed to load the report' };
    }
  }, [token]);

  // jobId: a job id, '__none__' for candidates not linked to a job, or
  // null/undefined for every job.
  const getCandidateReport = useCallback(async (jobId, period) => {
    try {
      const params = reportQuery(period, { job_id: jobId });
      const response = await axios.get(`${BACKEND_URL}/api/recruitment/reports/candidates?${params}`, { headers });
      return { success: true, data: response.data };
    } catch (err) {
      return { success: false, error: err.response?.data?.detail || 'Failed to load the report' };
    }
  }, [token]);

  // ============ JOBS ============
  const fetchJobs = useCallback(async (filters = {}) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.status) params.append('status', filters.status);
      if (filters.department) params.append('department', filters.department);
      
      const response = await axios.get(`${BACKEND_URL}/api/recruitment/jobs?${params.toString()}`, { headers });
      setJobs(response.data);
      return response.data;
    } catch (err) {
      console.error('Failed to fetch jobs:', err);
      return [];
    } finally {
      setLoading(false);
    }
  }, [token]);

  const createJob = useCallback(async (jobData) => {
    try {
      const response = await axios.post(`${BACKEND_URL}/api/recruitment/jobs`, jobData, { headers });
      await fetchJobs();
      return { success: true, data: response.data };
    } catch (err) {
      return { success: false, error: err.response?.data?.detail || 'Failed to create job' };
    }
  }, [token, fetchJobs]);

  const updateJob = useCallback(async (jobId, jobData) => {
    try {
      const response = await axios.put(`${BACKEND_URL}/api/recruitment/jobs/${jobId}`, jobData, { headers });
      await fetchJobs();
      return { success: true, data: response.data };
    } catch (err) {
      return { success: false, error: err.response?.data?.detail || 'Failed to update job' };
    }
  }, [token, fetchJobs]);

  const deleteJob = useCallback(async (jobId) => {
    try {
      await axios.delete(`${BACKEND_URL}/api/recruitment/jobs/${jobId}`, { headers });
      await fetchJobs();
      return { success: true };
    } catch (err) {
      return { success: false, error: err.response?.data?.detail || 'Failed to delete job' };
    }
  }, [token, fetchJobs]);

  // ============ CANDIDATES ============
  const fetchCandidates = useCallback(async (filters = {}) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.job_id) params.append('job_id', filters.job_id);
      if (filters.status) params.append('status', filters.status);
      if (filters.source) params.append('source', filters.source);
      if (filters.stage) params.append('stage', filters.stage);
      if (filters.tag) params.append('tag', filters.tag);
      if (filters.experience_min) params.append('experience_min', filters.experience_min);
      if (filters.experience_max) params.append('experience_max', filters.experience_max);
      if (filters.search) params.append('search', filters.search);
      if (filters.page) params.append('page', filters.page);
      if (filters.page_size) params.append('page_size', filters.page_size);
      
      const response = await axios.get(`${BACKEND_URL}/api/recruitment/candidates?${params.toString()}`, { headers });
      setCandidates(response.data);
      return response.data;
    } catch (err) {
      console.error('Failed to fetch candidates:', err);
      return [];
    } finally {
      setLoading(false);
    }
  }, [token]);

  const getCandidate = useCallback(async (candidateId) => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/recruitment/candidates/${candidateId}`, { headers });
      return response.data;
    } catch (err) {
      console.error('Failed to fetch candidate:', err);
      return null;
    }
  }, [token]);

  // Fetch ALL candidates by paginating (backend caps page_size at 200).
  // Use this for Kanban / JobDetail / InterviewList where ALL candidates matter.
  // Returns the full accumulated list AND updates the candidates state once at the end.
  // `silent: true` returns the rows WITHOUT touching shared context state
  // (neither `candidates` nor `loading`). Default behaviour is unchanged.
  //
  // It exists because this is not a pure fetch: it overwrites the shared
  // `candidates` list. A caller that only wants the rows for its own local use
  // — JobDetail's "Link existing" search — would otherwise replace whatever
  // the context held (that job's candidates) with every candidate in the
  // system, and flash the shared loading flag.
  const fetchAllCandidates = useCallback(async (filters = {}, { silent = false } = {}) => {
    if (!silent) setLoading(true);
    try {
      const PAGE_SIZE = 200;
      const MAX_PAGES = 50; // hard safety cap (10,000 candidates)
      const buildUrl = (page) => {
        const params = new URLSearchParams();
        if (filters.job_id) params.append('job_id', filters.job_id);
        if (filters.status) params.append('status', filters.status);
        if (filters.source) params.append('source', filters.source);
        if (filters.stage) params.append('stage', filters.stage);
        if (filters.tag) params.append('tag', filters.tag);
        if (filters.experience_min) params.append('experience_min', filters.experience_min);
        if (filters.experience_max) params.append('experience_max', filters.experience_max);
        if (filters.search) params.append('search', filters.search);
        params.append('page', String(page));
        params.append('page_size', String(PAGE_SIZE));
        return `${BACKEND_URL}/api/recruitment/candidates?${params.toString()}`;
      };

      const all = [];
      for (let page = 1; page <= MAX_PAGES; page += 1) {
        // eslint-disable-next-line no-await-in-loop
        const resp = await axios.get(buildUrl(page), { headers });
        const batch = Array.isArray(resp.data) ? resp.data : [];
        all.push(...batch);
        if (batch.length < PAGE_SIZE) break; // last page reached
      }
      if (!silent) setCandidates(all);
      return all;
    } catch (err) {
      console.error('Failed to fetch all candidates:', err);
      return [];
    } finally {
      if (!silent) setLoading(false);
    }
  }, [token]);

  const createCandidate = useCallback(async (candidateData) => {
    try {
      const response = await axios.post(`${BACKEND_URL}/api/recruitment/candidates`, candidateData, { headers });
      await fetchCandidates();
      return { success: true, data: response.data };
    } catch (err) {
      return { success: false, error: err.response?.data?.detail || 'Failed to create candidate' };
    }
  }, [token, fetchCandidates]);

  const updateCandidate = useCallback(async (candidateId, candidateData) => {
    try {
      const response = await axios.put(`${BACKEND_URL}/api/recruitment/candidates/${candidateId}`, candidateData, { headers });
      await fetchCandidates();
      return { success: true, data: response.data };
    } catch (err) {
      return { success: false, error: err.response?.data?.detail || 'Failed to update candidate' };
    }
  }, [token, fetchCandidates]);

  // `refresh: false` skips the post-update reload. Default is unchanged, so
  // CandidateDetail and JobDetail behave exactly as before.
  //
  // The Kanban opts out because this reload is UNFILTERED and first-page-only:
  // on a board filtered to one job, every drag replaced the shared list with
  // the newest candidates across ALL jobs. The board now moves the card itself
  // (see KanbanBoard.handleDrop) and reloads with its own filter on failure.
  const updateCandidateStage = useCallback(async (candidateId, stage, notes = '', { refresh = true } = {}) => {
    try {
      const params = new URLSearchParams({ stage });
      if (notes) params.append('notes', notes);

      const response = await axios.put(
        `${BACKEND_URL}/api/recruitment/candidates/${candidateId}/stage?${params.toString()}`,
        {},
        { headers }
      );
      if (refresh) await fetchCandidates();
      return { success: true, data: response.data };
    } catch (err) {
      return { success: false, error: err.response?.data?.detail || 'Failed to update stage' };
    }
  }, [token, fetchCandidates]);

  // Apply a local patch to one candidate in the shared list, matched by id.
  // Functional update, so concurrent patches to DIFFERENT candidates (two quick
  // drags) never overwrite each other. A no-op if the id is not in the list.
  const patchCandidateLocal = useCallback((candidateId, patch) => {
    setCandidates((prev) => prev.map((c) => (c.id === candidateId ? { ...c, ...patch } : c)));
  }, []);

  const deleteCandidate = useCallback(async (candidateId) => {
    try {
      await axios.delete(`${BACKEND_URL}/api/recruitment/candidates/${candidateId}`, { headers });
      await fetchCandidates();
      return { success: true };
    } catch (err) {
      return { success: false, error: err.response?.data?.detail || 'Failed to delete candidate' };
    }
  }, [token, fetchCandidates]);

  // ============ RESUME PARSING ============
  // Multipart uploads deliberately DON'T set Content-Type or Authorization
  // manually. Two reasons:
  //   1. Content-Type: for FormData bodies, the browser (or axios) auto-
  //      sets Content-Type: multipart/form-data; boundary=<generated>. If
  //      we pin the header to a bare "multipart/form-data" here the
  //      boundary is missing and the backend's parser stalls or silently
  //      rejects. That was the pre-Phase-A behaviour we just inherited.
  //   2. Authorization: the useAuthRefresh request interceptor already
  //      writes a live-from-tokenRef Bearer header on every /api/recruitment
  //      call, so a manually-passed Authorization here fights the
  //      interceptor and, on retry-after-refresh, races it. Leaving it off
  //      lets the interceptor own the header end-to-end.
  const parseResume = useCallback(async (file, jobId = null) => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      if (jobId) formData.append('job_id', jobId);

      const response = await axios.post(
        `${BACKEND_URL}/api/recruitment/candidates/parse-resume`,
        formData,
      );
      return { success: true, data: response.data };
    } catch (err) {
      return { success: false, error: err.response?.data?.detail || 'Failed to parse resume' };
    }
  }, [token]);

  const bulkUploadResumes = useCallback(async (files, jobId = null) => {
    try {
      const formData = new FormData();
      files.forEach(file => formData.append('files', file));
      if (jobId) formData.append('job_id', jobId);

      const response = await axios.post(
        `${BACKEND_URL}/api/recruitment/candidates/bulk-upload`,
        formData,
      );
      return { success: true, data: response.data };
    } catch (err) {
      return { success: false, error: err.response?.data?.detail || 'Failed to upload resumes' };
    }
  }, [token]);

  // ============ RESUME MANAGEMENT ============
  const uploadResume = useCallback(async (candidateId, file) => {
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(
        `${BACKEND_URL}/api/recruitment/candidates/${candidateId}/upload-resume`,
        formData,
      );
      return { success: true, data: response.data };
    } catch (err) {
      return { success: false, error: err.response?.data?.detail || 'Failed to upload resume' };
    }
  }, [token]);

  // Fetch the resume PDF over axios (so the silent-refresh interceptor
  // supplies a live Bearer token) and hand back a browser blob URL. The
  // caller sets iframe.src / anchor.href to the blob URL and revokes it
  // on unmount. This replaces the old ?token=<JWT> query pattern that
  // failed with "Invalid or expired token" 15 min into a session — the
  // URL was cached in a useCallback closure with a token that had since
  // expired, and iframe fetches bypass the axios interceptor so nothing
  // refreshed it.
  const fetchResumeBlobUrl = useCallback(async (candidateId) => {
    try {
      const r = await axios.get(
        `${BACKEND_URL}/api/recruitment/candidates/${candidateId}/resume`,
        { responseType: 'blob' },
      );
      const blob = new Blob([r.data], { type: 'application/pdf' });
      return { success: true, url: URL.createObjectURL(blob) };
    } catch (err) {
      return { success: false, error: err.response?.data?.detail || 'Failed to load resume' };
    }
  }, [token]);

  // Kept for any legacy caller that still expects a plain URL. Prefer
  // fetchResumeBlobUrl in new code — this returns a URL with a
  // closure-captured token that will 401 once the token TTL passes.
  const getResumeUrl = useCallback((candidateId) => {
    return `${BACKEND_URL}/api/recruitment/candidates/${candidateId}/resume?token=${token}`;
  }, [token]);

  const deleteResume = useCallback(async (candidateId) => {
    try {
      await axios.delete(`${BACKEND_URL}/api/recruitment/candidates/${candidateId}/resume`, { headers });
      return { success: true };
    } catch (err) {
      return { success: false, error: err.response?.data?.detail || 'Failed to delete resume' };
    }
  }, [token]);

  // ============ PIPELINE TEMPLATES ============
  const fetchPipelineTemplates = useCallback(async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/recruitment/pipeline-templates`, { headers });
      setPipelineTemplates(response.data);
      return response.data;
    } catch (err) {
      console.error('Failed to fetch pipeline templates:', err);
      return [];
    }
  }, [token]);

  const createPipelineTemplate = useCallback(async (templateData) => {
    try {
      const response = await axios.post(`${BACKEND_URL}/api/recruitment/pipeline-templates`, templateData, { headers });
      await fetchPipelineTemplates();
      return { success: true, data: response.data };
    } catch (err) {
      return { success: false, error: err.response?.data?.detail || 'Failed to create template' };
    }
  }, [token, fetchPipelineTemplates]);

  const updatePipelineTemplate = useCallback(async (templateId, templateData) => {
    try {
      const response = await axios.put(`${BACKEND_URL}/api/recruitment/pipeline-templates/${templateId}`, templateData, { headers });
      await fetchPipelineTemplates();
      return { success: true, data: response.data };
    } catch (err) {
      return { success: false, error: err.response?.data?.detail || 'Failed to update template' };
    }
  }, [token, fetchPipelineTemplates]);

  const deletePipelineTemplate = useCallback(async (templateId) => {
    try {
      await axios.delete(`${BACKEND_URL}/api/recruitment/pipeline-templates/${templateId}`, { headers });
      await fetchPipelineTemplates();
      return { success: true };
    } catch (err) {
      return { success: false, error: err.response?.data?.detail || 'Failed to delete template' };
    }
  }, [token, fetchPipelineTemplates]);

  // ============ INTERVIEWS ============
  const fetchInterviews = useCallback(async (filters = {}) => {
    try {
      const params = new URLSearchParams();
      if (filters.candidate_id) params.append('candidate_id', filters.candidate_id);
      if (filters.job_id) params.append('job_id', filters.job_id);
      if (filters.status) params.append('status', filters.status);
      if (filters.date) params.append('date', filters.date);
      
      const response = await axios.get(`${BACKEND_URL}/api/recruitment/interviews?${params.toString()}`, { headers });
      setInterviews(response.data);
      return response.data;
    } catch (err) {
      console.error('Failed to fetch interviews:', err);
      return [];
    }
  }, [token]);

  const createInterview = useCallback(async (interviewData) => {
    try {
      const response = await axios.post(`${BACKEND_URL}/api/recruitment/interviews`, interviewData, { headers });
      return { success: true, data: response.data };
    } catch (err) {
      return { success: false, error: err.response?.data?.detail || 'Failed to create interview' };
    }
  }, [token]);

  const updateInterview = useCallback(async (interviewId, interviewData) => {
    try {
      const response = await axios.put(`${BACKEND_URL}/api/recruitment/interviews/${interviewId}`, interviewData, { headers });
      return { success: true, data: response.data };
    } catch (err) {
      return { success: false, error: err.response?.data?.detail || 'Failed to update interview' };
    }
  }, [token]);

  const deleteInterview = useCallback(async (interviewId) => {
    try {
      await axios.delete(`${BACKEND_URL}/api/recruitment/interviews/${interviewId}`, { headers });
      return { success: true };
    } catch (err) {
      return { success: false, error: err.response?.data?.detail || 'Failed to delete interview' };
    }
  }, [token]);

  // ============ OFFERS ============
  const fetchOffers = useCallback(async (candidateId = null) => {
    try {
      const params = candidateId ? `?candidate_id=${candidateId}` : '';
      const response = await axios.get(`${BACKEND_URL}/api/recruitment/offers${params}`, { headers });
      return response.data;
    } catch (err) {
      console.error('Failed to fetch offers:', err);
      return [];
    }
  }, [token]);

  const createOffer = useCallback(async (offerData) => {
    try {
      const response = await axios.post(`${BACKEND_URL}/api/recruitment/offers`, offerData, { headers });
      return { success: true, data: response.data };
    } catch (err) {
      return { success: false, error: err.response?.data?.detail || 'Failed to create offer' };
    }
  }, [token]);

  const updateOffer = useCallback(async (offerId, offerData) => {
    try {
      const response = await axios.put(`${BACKEND_URL}/api/recruitment/offers/${offerId}`, offerData, { headers });
      return { success: true, data: response.data };
    } catch (err) {
      return { success: false, error: err.response?.data?.detail || 'Failed to update offer' };
    }
  }, [token]);

  // ============ CONVERT TO EMPLOYEE ============
  const convertToEmployee = useCallback(async (candidateId, employeeId = null) => {
    try {
      const params = employeeId ? `?employee_id=${employeeId}` : '';
      const response = await axios.post(`${BACKEND_URL}/api/recruitment/candidates/${candidateId}/convert-to-employee${params}`, {}, { headers });
      await fetchCandidates();
      return { success: true, data: response.data };
    } catch (err) {
      return { success: false, error: err.response?.data?.detail || 'Failed to convert to employee' };
    }
  }, [token, fetchCandidates]);

  // ============ EMPLOYEES LOOKUP ============
  const fetchEmployeesLookup = useCallback(async (search = '') => {
    try {
      const params = search ? `?search=${encodeURIComponent(search)}` : '';
      const response = await axios.get(`${BACKEND_URL}/api/recruitment/employees-lookup${params}`, { headers });
      setEmployees(response.data);
      return response.data;
    } catch (err) {
      console.error('Failed to fetch employees:', err);
      return [];
    }
  }, [token]);

  // ============ SOURCES & STATUSES ============
  const getSources = useCallback(async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/recruitment/sources`, { headers });
      return response.data;
    } catch (err) {
      return [];
    }
  }, [token]);

  const getStatuses = useCallback(async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/recruitment/statuses`, { headers });
      return response.data;
    } catch (err) {
      return [];
    }
  }, [token]);

  // ============ TAGS ============
  const fetchTags = useCallback(async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/recruitment/tags`, { headers });
      return response.data;
    } catch (err) {
      return [];
    }
  }, [token]);

  // ============ AUDIT LOGS ============
  const getAuditLogs = useCallback(async (page = 1, pageSize = 50, entity = null, action = null, fromDate = null, toDate = null) => {
    try {
      const params = new URLSearchParams({ page, page_size: pageSize });
      if (entity) params.append('entity', entity);
      if (action) params.append('action', action);
      if (fromDate) params.append('from_date', fromDate);
      if (toDate) params.append('to_date', toDate);
      
      const response = await axios.get(`${BACKEND_URL}/api/recruitment/audit-logs?${params.toString()}`, { headers });
      return response.data;
    } catch (err) {
      console.error('Failed to fetch audit logs:', err);
      return { data: [], pagination: { page: 1, page_size: 50, total: 0, total_pages: 0 } };
    }
  }, [token]);

  const value = {
    loading,
    isReady,
    jobs,
    candidates,
    pipelineTemplates,
    interviews,
    employees,
    // Dashboard
    getDashboardStats,
    getEnhancedDashboardStats,
    // Reports
    getStageSummaryReport,
    getStageFunnelReport,
    getCandidateReport,
    // Jobs
    fetchJobs,
    createJob,
    updateJob,
    deleteJob,
    // Candidates
    fetchCandidates,
    fetchAllCandidates,
    getCandidate,
    createCandidate,
    updateCandidate,
    updateCandidateStage,
    patchCandidateLocal,
    deleteCandidate,
    // Resume
    parseResume,
    bulkUploadResumes,
    uploadResume,
    getResumeUrl,
    fetchResumeBlobUrl,
    deleteResume,
    // Pipeline Templates
    fetchPipelineTemplates,
    createPipelineTemplate,
    updatePipelineTemplate,
    deletePipelineTemplate,
    // Interviews
    fetchInterviews,
    createInterview,
    updateInterview,
    deleteInterview,
    // Offers
    fetchOffers,
    createOffer,
    updateOffer,
    // Convert
    convertToEmployee,
    // Lookups
    fetchEmployeesLookup,
    getSources,
    getStatuses,
    // Tags
    fetchTags,
    // Audit
    getAuditLogs
  };

  return (
    <RecruitmentContext.Provider value={value}>
      {children}
    </RecruitmentContext.Provider>
  );
};

export default RecruitmentContext;
